import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface TrashItem {
  id: string;
  type: 'item' | 'spec' | 'order';
  name: string;
  deleted_at: string;
  deleted_by: string | null;
  project_id: string;
  project_name: string;
  days_remaining: number;
}

/**
 * Fetch all trash items (soft-deleted items, specs, orders)
 */
export const useTrashItems = (projectId?: string) => {
  return useQuery({
    queryKey: ['trash', projectId],
    queryFn: async (): Promise<TrashItem[]> => {
      const args = { project_id_param: projectId || null };
      if (import.meta.env.DEV) {
        console.log('[Trash] get_trash_items args:', Object.keys(args));
      }
      const { data, error } = await supabase.rpc('get_trash_items', args);

      if (error) throw error;
      
      // Calculate days_remaining from deleted_at (30 days retention)
      const now = new Date();
      return (data || []).map((item: any) => {
        const deletedAt = new Date(item.deleted_at);
        const diffDays = Math.floor((now.getTime() - deletedAt.getTime()) / (1000 * 60 * 60 * 24));
        return {
          ...item,
          days_remaining: Math.max(0, 30 - diffDays),
          deleted_by: item.deleted_by || null
        } as TrashItem;
      });
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Restore an item from trash
 */
export const useRestoreItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('restore_item', {
        item_id_param: id
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; name?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to restore item');
      }
      
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['trash'] });
      queryClient.invalidateQueries({ queryKey: ['items-optimized'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      
      toast.success('Đã khôi phục item', {
        description: data.name ? `"${data.name}" đã được khôi phục` : undefined
      });
    },
    onError: (error: Error) => {
      toast.error('Lỗi khôi phục item', {
        description: error.message
      });
    }
  });
};

/**
 * Restore a spec from trash
 */
export const useRestoreSpec = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('restore_spec', {
        spec_id_param: id
      });

      if (error) throw error;
      
      if (data !== true) {
        throw new Error('Failed to restore spec');
      }
      
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trash'] });
      queryClient.invalidateQueries({ queryKey: ['diary-specs'] });
      
      toast.success('Đã khôi phục spec');
    },
    onError: (error: Error) => {
      if (error.message.includes('SPECS_PERMISSION_DENIED')) {
        toast.error('Không có quyền khôi phục spec', {
          description: 'Bạn cần quyền truy cập Specs để khôi phục'
        });
      } else {
        toast.error('Lỗi khôi phục spec', {
          description: error.message
        });
      }
    }
  });
};

/**
 * Restore an order from trash
 */
export const useRestoreOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('restore_order', {
        order_id_param: id
      });

      if (error) throw error;
      
      if (data !== true) {
        throw new Error('Failed to restore order');
      }
      
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trash'] });
      queryClient.invalidateQueries({ queryKey: ['procurement'] });
      
      toast.success('Đã khôi phục đơn hàng');
    },
    onError: (error: Error) => {
      if (error.message.includes('PROCUREMENT_PERMISSION_DENIED')) {
        toast.error('Không có quyền khôi phục đơn hàng', {
          description: 'Bạn cần quyền truy cập Procurement để khôi phục'
        });
      } else if (error.message.includes('ORDER_CODE_CONFLICT')) {
        toast.error('Không thể khôi phục đơn hàng', {
          description: 'Mã đơn hàng đã được dùng trong dự án. Vui lòng đổi mã trước khi khôi phục.',
          duration: 5000
        });
      } else {
        toast.error('Lỗi khôi phục đơn hàng', {
          description: error.message
        });
      }
    }
  });
};

/**
 * Permanently delete a trash item (admin only)
 */
export const usePermanentDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, type }: { id: string; type: TrashItem['type'] }) => {
      const { data, error } = await supabase.rpc('hard_delete_trash_item', {
        item_id_param: id,
        item_type_param: type
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; message?: string; name?: string };
      if (!result.success) {
        if (result.error === 'PERMISSION_DENIED') {
          throw new Error(result.message || 'Không có quyền xóa vĩnh viễn');
        }
        throw new Error(result.error || 'Failed to delete permanently');
      }
      
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['trash'] });
      toast.success('Đã xóa vĩnh viễn', {
        description: data.name ? `"${data.name}" đã bị xóa hoàn toàn` : undefined
      });
    },
    onError: (error: Error) => {
      toast.error('Lỗi xóa vĩnh viễn', {
        description: error.message
      });
    }
  });
};

/**
 * Helper to restore any trash item by type
 */
export const useRestoreTrashItem = () => {
  const restoreItem = useRestoreItem();
  const restoreSpec = useRestoreSpec();
  const restoreOrder = useRestoreOrder();

  return useMutation({
    mutationFn: async ({ id, type }: { id: string; type: TrashItem['type'] }) => {
      switch (type) {
        case 'item':
          return restoreItem.mutateAsync(id);
        case 'spec':
          return restoreSpec.mutateAsync(id);
        case 'order':
          return restoreOrder.mutateAsync(id);
        default:
          throw new Error(`Unknown type: ${type}`);
      }
    }
  });
};
